import {
  Component,
  OnInit,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DeviceService } from '@shared/services/device.service';
import { QzService } from '@shared/services/qz.service';
import { PrintNodeService } from '@shared/services/print-node.service';
import { ToastService } from '@shared/services/toast.service';
import { AppService } from '@shared/services/app.service';
import { PrintJobListenerService } from '@shared/services/websocket/print-job-listener.service';
import { SucursalService } from '@shared/services/sucursal.service';
import { ImpresoraApiService } from '@core/services/api/impresora-api.service';
import { ICrearImpresoraRq } from '@core/interfaces/request-response/impresora.dto.interface';
import { IConfiguracionImpresora } from '@core/interfaces/entidades/configuracion-impresora.entidad';
import { logger } from '@shared/utils/functions.utils';

@Component({
  selector: 'app-config-punto-impresion',
  imports: [FormsModule],
  templateUrl: './config-punto-impresion.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './config-punto-impresion.css',
})
export class ConfigPuntoImpresion implements OnInit {
  constructor() {
    effect(() => {
      if (this.appService.authReady() && this.sucursalService.sucursalReady()) {
        this.inicializar();
      }
    });
  }
  ngOnInit(): void {
    this.appService.setPanelHeader({
      title: 'Punto de Impresión',
      subtitle: 'Configura tu impresora para impresión directa',
      showBackButton: true,
      icon: 'bi bi-printer-fill text-3xl',
    });

    this.qzService.impresoras$.subscribe((impresoras) => {
      this.impresoras.set(impresoras);
    });
  }
  // servicios
  private readonly appService = inject(AppService);
  private readonly sucursalService = inject(SucursalService);
  private readonly deviceService = inject(DeviceService);
  private readonly qzService = inject(QzService);
  private readonly printNodeService = inject(PrintNodeService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly impresoraApiService = inject(ImpresoraApiService);
  private readonly printJobListener = inject(PrintJobListenerService);

  // ==================== Estado ====================
  isDesktop = computed(() => this.deviceService.isDesktop());
  isBrowser = computed(() => isPlatformBrowser(this.platformId));

  impresoras = signal<string[] | null>(null);
  impresoraSeleccionada = signal<string | null>(null);
  // inputs para guardar configuración
  aliasImpresora = signal<string>('');
  width_mm_Impresora = signal<number>(80);

  configActual = signal<{
    printerName: string;
    aliasImpresora: string;
    configImpresoraId: number;
    sucursalId: number;
    widthMm: number;
  } | null>(null);

  testPrintLoading = signal(false);

  // ==================== Propiedades Computadas ====================
  puedeConfigurar = computed(() => this.isBrowser() && this.isDesktop());
  tieneConfiguracion = computed(() => !!this.configActual());
  configImpresoraId = computed(() => this.configActual()?.configImpresoraId ?? null);

  sucursal_actual = this.sucursalService.activeSucursal;
  // ==================== Inicialización ====================
  private async inicializar() {
    if (!this.sucursal_actual() || !this.sucursal_actual()?.id) {
      return;
    }
    if (!this.isDesktop()) {
      this.toastService.show(
        'top-right',
        'warn',
        'Dispositivo no compatible',
        'La configuración del punto de impresión solo está disponible en dispositivos de escritorio.',
      );
      return;
    }
    this.cargarConfiguracionGuardada();
  }

  private cargarConfiguracionGuardada() {
    const sucursal = this.sucursal_actual();
    if (!sucursal) {
      return;
    }

    const config = this.printNodeService.getConfigBySucursal(sucursal.id);
    this.applyConfig(config ?? null);
  }

  // ==================== Acciones ====================
  seleccionarImpresora(printerName: string) {
    this.impresoraSeleccionada.set(printerName);
  }

  guardarConfiguracion() {
    const sucursal = this.sucursal_actual();
    if (!sucursal?.id) {
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No hay una sucursal activa para guardar la configuración',
      );
      return;
    }

    const printerName = this.impresoraSeleccionada();
    const alias = this.aliasImpresora().trim();
    const width_mm = this.width_mm_Impresora();

    if (!printerName) {
      this.toastService.show('top-right', 'warn', 'Advertencia', 'Selecciona una impresora');
      return;
    }

    if (!alias) {
      this.toastService.show(
        'top-right',
        'warn',
        'Advertencia',
        'Ingresa un alias para la impresora (puede ser el mismo nombre técnico o uno personalizado)',
      );
      return;
    }
    const rq: ICrearImpresoraRq = {
      nombre: alias,
      nombre_impresora_qz: printerName,
      width_mm: width_mm,
    };
    this.impresoraApiService.guardarConfigImpresora(rq).subscribe({
      next: (rs: IConfiguracionImpresora) => {
        const config = {
          printerName: rs.nombre_impresora_qz,
          aliasImpresora: alias,
          configImpresoraId: rs.id,
          sucursalId: sucursal.id,
          widthMm: rs.width_mm,
        };

        this.printNodeService.saveConfig(config);
        this.applyConfig(config);
        this.printJobListener.start(sucursal.id);

        this.toastService.show(
          'top-right',
          'success',
          'Configuración guardada',
          `Impresora "${alias}" configurada correctamente`,
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        console.error('Error guardando configuración:', err);
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err?.error?.message || 'No se pudo guardar la configuración',
        );
      },
    });
  }

  async testImpresion() {
    const config = this.configActual();

    if (!config) {
      this.toastService.show('top-right', 'warn', 'Advertencia', 'No hay configuración guardada');
      return;
    }

    this.testPrintLoading.set(true);
    try {
      if (this.qzService.isVirtualPdfPrinter(config.printerName)) {
        const html = `
          <div style="font-family: monospace; padding: 12px;">
            <h3 style="margin: 0 0 8px 0;">VentaClick - Test de Impresión</h3>
            <p style="margin: 4px 0;">Fecha: ${new Date().toLocaleString('es-ES')}</p>
            <p style="margin: 4px 0;">Impresora: ${config.printerName}</p>
            <hr />
            <p style="margin: 4px 0;">Si ves este contenido, el flujo PDF funciona correctamente.</p>
          </div>
        `;

        await this.qzService.printHtmlWithBrowserDialog(html, 'Test Impresion PDF');
      } else {
        const text = [
          '\x1B\x40', // Reset impresora
          'VentaClick - Test de Impresión\n',
          `Fecha: ${new Date().toLocaleString('es-ES')}\n`,
          '--------------------------------\n',
          `Impresora: ${config.printerName}\n`,
          '--------------------------------\n',
          '\n',
          'Si ves este texto,\n',
          'la impresora funciona correctamente.\n',
          // cortar
          '\x1D\x56\x00',
        ];
        logger('Enviando test de impresión a:', config.printerName, { text });
        await this.qzService.printData(config.printerName, text);
      }

      this.toastService.show(
        'top-right',
        'success',
        'Test enviado',
        `Se envió un test de impresión a "${config.printerName}"`,
      );
    } catch (error) {
      console.error('Error en test de impresión:', error);
      this.toastService.show(
        'top-right',
        'error',
        'Error',
        'No se pudo ejecutar el test de impresión. Asegúrate que QZ Tray esté activo y la impresora conectada.',
      );
    } finally {
      this.testPrintLoading.set(false);
    }
  }

  limpiarConfiguracion() {
    this.impresoraApiService.eliminarConfigImpresora(this.configImpresoraId()!).subscribe({
      next: () => {
        const activeSucursalId = this.sucursalService.activeSucursal()?.id;
        if (activeSucursalId) {
          this.printNodeService.clearBySucursal(activeSucursalId);
        }
        this.printJobListener.stop();
        this.applyConfig(null);

        this.toastService.show(
          'top-right',
          'success',
          'Configuración eliminada',
          'Se limpió la configuración del punto de impresión',
        );
      },
      error: (err) => {
        if (err.status == 401) return;
        console.error('Error eliminando configuración:', err);
        this.toastService.show(
          'top-right',
          'error',
          'Error',
          err?.error?.message || 'No se pudo eliminar la configuración',
        );
      },
    });
  }

  // ==================== Utilidades ====================
  private applyConfig(
    config: {
      printerName: string;
      aliasImpresora: string;
      configImpresoraId: number;
      sucursalId: number;
      widthMm: number;
    } | null,
  ) {
    if (config) {
      this.configActual.set(config);
      this.impresoraSeleccionada.set(config.printerName);
      this.aliasImpresora.set(config.aliasImpresora || config.printerName);
      this.width_mm_Impresora.set(config.widthMm);
      return;
    }

    this.configActual.set(null);
    this.impresoraSeleccionada.set(null);
    this.aliasImpresora.set('');
    this.width_mm_Impresora.set(80);
  }
}
